import * as React from "react";
import { DatasheetElementSettingsFieldComponentValue, DatasheetElementSettingsTextComponentValue } from "../../components/datasheet-element-settings/controllers/main";
import { TableValue } from "../../components/datasheet-element-settings/controllers/table";
import { isFieldTypeTabNavigable, NON_TAB_FIELD_TYPES } from "@modules/datasheets/utils/tab-navigation";
import { DatasheetElement, DatasheetElementId, DatasheetElementType } from "@modules/datasheets/types/datasheet-element";
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetTextComponentGetDto } from "@common/dto/datasheet/text-component/get-dto";
import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";
import { DatasheetFieldComponentHeadingPosition } from "@common/dto/datasheet/field-component/heading-position";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";

type Props = {
  normalizeDatasheetElementsTabOrder: (elements: Record<DatasheetElementId, DatasheetElement>) => Record<DatasheetElementId, DatasheetElement>;
  datasheetElements: Record<DatasheetElementId, DatasheetElement>;
  getDatasheetElement: (id: DatasheetElementId) => DatasheetElement | undefined;
  selectedDatasheetElementsId: Array<DatasheetElementId>;
  onDatasheetElementsChange: (datasheetElements: Record<DatasheetElementId, DatasheetElement>) => void;
};

export const useDatasheetElementSettingsController = (props: Props) => {
  const {
    selectedDatasheetElementsId,
    datasheetElements,
    getDatasheetElement,
    onDatasheetElementsChange,
    normalizeDatasheetElementsTabOrder
  } = props;

  const tabEligibleFieldCount = React.useMemo(() =>
    Object.values(datasheetElements).filter((el) => {
      if (el.type !== DatasheetElementType.field) return false;
      const f = (el as DatasheetElement<DatasheetFieldComponentGetDto>).original.field;
      const ft = (f as any)?.fieldtype?.typeid ?? (f as any)?.type ?? (f as any)?.rawFieldType;
      return isFieldTypeTabNavigable(ft, Boolean(f?.system));
    }).length,
  [datasheetElements]);

  const convertNumberToTextStyleArray = React.useCallback((textStyleNumber: number | Array<number> | undefined): Array<DatasheetComponentTextStyle> => {
    if (Array.isArray(textStyleNumber)) {
      return textStyleNumber.filter((s): s is DatasheetComponentTextStyle => 
        s === DatasheetComponentTextStyle.bold || 
        s === DatasheetComponentTextStyle.italic || 
        s === DatasheetComponentTextStyle.underline
      );
    }
    if (typeof textStyleNumber !== 'number' || textStyleNumber === 0 || textStyleNumber === 33) return [];
    
    // Decode number back to array of styles using bitwise AND checks
    // Enum values: bold=4, italic=2, underline=16 (matching Java DomConstants bit masks)
    const styles: Array<DatasheetComponentTextStyle> = [];
    
    // Use bitwise AND to check which flags are set
    if ((textStyleNumber & DatasheetComponentTextStyle.bold) === DatasheetComponentTextStyle.bold) {
      styles.push(DatasheetComponentTextStyle.bold);
    }
    if ((textStyleNumber & DatasheetComponentTextStyle.italic) === DatasheetComponentTextStyle.italic) {
      styles.push(DatasheetComponentTextStyle.italic);
    }
    if ((textStyleNumber & DatasheetComponentTextStyle.underline) === DatasheetComponentTextStyle.underline) {
      styles.push(DatasheetComponentTextStyle.underline);
    }
    
    return styles;
  }, []);

  const mapTextComponentSettingsToElement = React.useCallback((value: DatasheetElementSettingsTextComponentValue): Partial<DatasheetTextComponentGetDto> => {
    // Store as array for UI to properly track multiple styles, convert to number only when saving
    const result: Partial<DatasheetTextComponentGetDto> = {
      textstyle: value.textStyle as any, // Store as array in element for proper UI handling
      fontsize: value.fontSize,
      fontname: value.fontFamily,
      overStrike: false,
      dashes: false,
      verticalLine: false,
      text: value.text,
      align: value.align ?? DatasheetFieldComponentTextAlign.left,
    };
    
    // Only convert color if it's provided and is a valid hex color
    if (value.color && typeof value.color === 'string' && value.color.startsWith('#')) {
      // Store hexColor first (already in RGB format)
      result.hexColor = value.color;
      // Convert RGB hex to BGR number for fontColor (swap bytes: RRGGBB -> BBGGRR)
      const hex = value.color.replace('#', '');
      if (hex.length === 6) {
        const bgr = hex.substring(4, 6) + hex.substring(2, 4) + hex.substring(0, 2);
        result.fontColor = parseInt(bgr, 16) || 0;
      } else {
        result.fontColor = parseInt(hex, 16) || 0;
      }
    }
    
    return result;
  }, []);

  const mapFieldComponentSettingsToElement = React.useCallback((value: DatasheetElementSettingsFieldComponentValue): Partial<DatasheetFieldComponentGetDto> => {
    return {
      fontname: value.content.fontFamily,
      fontsize: value.content.fontSize,
      fontcolor: (() => {
        // Store as RGB integer (no byte swapping)
        const hex = value.content.color.replace('#', '');
        const parsed = parseInt(hex, 16);
        return isNaN(parsed) ? 0 : parsed;
      })(),
      showfieldtype: value.content.showFieldType,
      textstyle: value.content.styles,
      contentalign: value.content.align,
      tabOrder: value.content.tabOrder,
      showborder: value.content.showBorder,

      headfont: value.heading.fontFamily,
      headfontsize: value.heading.fontSize,
      headcolor: (() => {
        // Store as RGB integer (no byte swapping). Default to black on invalid.
        if (!value.heading.color || typeof value.heading.color !== 'string' || !value.heading.color.startsWith('#')) {
          return 0; // Default to black
        }
        const hex = value.heading.color.replace('#', '');
        const parsed = parseInt(hex, 16);
        return isNaN(parsed) ? 0 : parsed;
      })(),
      headpos: value.heading.position,
      headalign: value.heading.align,
      headstyle: value.heading.textStyles,
      headtext: value.heading.textValue,
      headdisplay: value.heading.displayHeading,

      helptext: value.helpText.textValue,
    };
  }, []);

  const mapElementToFieldComponentSettings = React.useCallback((element: DatasheetElement<DatasheetFieldComponentGetDto>): DatasheetElementSettingsFieldComponentValue => {
    return {
      content: {
        showBorder: element.original.showborder,
        fontFamily: element.original.fontname,
        fontSize: element.original.fontsize,
        color: (() => {
          // Extract BGR bytes from number and output as RGB hex
          const fontcolorNum = typeof element.original.fontcolor === 'number' ? element.original.fontcolor : 0;
          const blue = (fontcolorNum & 0xFF);
          const green = ((fontcolorNum >> 8) & 0xFF);
          const red = ((fontcolorNum >> 16) & 0xFF);
          const hex = [
            red.toString(16).padStart(2, '0'),
            green.toString(16).padStart(2, '0'),
            blue.toString(16).padStart(2, '0')
          ].join('');
          return `#${hex}`;
        })(),
        showFieldType: element.original.showfieldtype,
        styles: element.original.textstyle,
        align: element.original.contentalign,
        tabOrder: element.original.tabOrder,
      },
      heading: {
        displayHeading: element.original.headdisplay,
        textValue: (() => {
          // If headdisplay is false, show field name (like in text-field component)
          if (!element.original.headdisplay) {
            return element.original.headingpds && element.original.headingpds !== "null" 
              ? element.original.headtext 
              : (element.original.field?.fieldname || element.original.headtext || '');
          }
          return element.original.headtext || '';
        })(),
        fontFamily: element.original.headfont ?? 'Tahoma',
        fontSize: element.original.headfontsize ?? 8,
        color: (() => {
          // Extract BGR bytes from number and output as RGB hex
          // Default to 0 (black) if headcolor is undefined, null, or not a number
          const headcolorNum = typeof element.original.headcolor === 'number' ? element.original.headcolor : 0;
          const blue = (headcolorNum & 0xFF);
          const green = ((headcolorNum >> 8) & 0xFF);
          const red = ((headcolorNum >> 16) & 0xFF);
          const hex = [
            red.toString(16).padStart(2, '0'),
            green.toString(16).padStart(2, '0'),
            blue.toString(16).padStart(2, '0')
          ].join('');
          return `#${hex}`;
        })(),
        position: element.original.headpos ?? DatasheetFieldComponentHeadingPosition.left,
        align: element.original.headalign ?? DatasheetFieldComponentTextAlign.left,
        textStyles: element.original.headstyle ?? [],
      },
      helpText: {
        textValue: element.original.helptext,
      },
      table: {
        tableReportFormat: element.original.tableReportFormat ?? null,
        dssubfields: element.original.dssubfields ?? element.original.m_ds_subfields,
        fieldSubfields: element.original.field?.subfields,
      } as TableValue,
    };
  }, []);

  const mapElementToTextComponentSettings = React.useCallback((element: DatasheetElement<DatasheetTextComponentGetDto>): DatasheetElementSettingsTextComponentValue => {
    const textStyle = element.original.textstyle;
    const textStyleArray = convertNumberToTextStyleArray(textStyle);
    
    // Prioritize hexColor (already in RGB format) over fontColor (BGR format)
    let color = '#000000';
    if (element.original.hexColor && typeof element.original.hexColor === 'string' && element.original.hexColor.startsWith('#')) {
      color = element.original.hexColor;
    } else if (element.original.fontColor !== undefined && element.original.fontColor !== null) {
      // Extract BGR bytes from number and output as RGB hex
      const fontColorNum = typeof element.original.fontColor === 'number' ? element.original.fontColor : 0;
      const blue = (fontColorNum & 0xFF);
      const green = ((fontColorNum >> 8) & 0xFF);
      const red = ((fontColorNum >> 16) & 0xFF);
      const hex = [
        red.toString(16).padStart(2, '0'),
        green.toString(16).padStart(2, '0'),
        blue.toString(16).padStart(2, '0')
      ].join('');
      color = `#${hex}`;
    }
    
    return {
      fontFamily: element.original.fontname || 'Arial',
      fontSize: element.original.fontsize || 14,
      color,
      textStyle: textStyleArray,
      text: element.original.text || '',
      align: element.original.align ?? DatasheetFieldComponentTextAlign.left,
    };
  }, [convertNumberToTextStyleArray]);

  const handleTextComponentSettingsChange = React.useCallback((value: DatasheetElementSettingsTextComponentValue) => {
    const updatedElements = selectedDatasheetElementsId.reduce<Record<DatasheetElementId, DatasheetElement>>((acc, elementId) => {
      const element = getDatasheetElement(elementId) as DatasheetElement<DatasheetTextComponentGetDto> | undefined;

      if (element?.type !== DatasheetElementType.text) {
        return acc;
      }

      const mappedSettings = mapTextComponentSettingsToElement(value);
      
      // Preserve existing hexColor if color wasn't explicitly changed
      // This ensures color doesn't get corrupted when only textStyle changes
      const updatedOriginal = {
        ...element.original,
        ...mappedSettings,
        // If hexColor exists in original and color wasn't provided in value, preserve it
        hexColor: mappedSettings.hexColor !== undefined 
          ? mappedSettings.hexColor 
          : element.original.hexColor,
      };

      const updatedElement: DatasheetElement = {
        ...element,
        original: updatedOriginal,
      };

      acc[element.id] = updatedElement;

      return acc;
    }, {});

    const mergedElements = {
      ...datasheetElements,
      ...updatedElements, 
    };

    onDatasheetElementsChange(mergedElements);
  }, [selectedDatasheetElementsId, onDatasheetElementsChange, getDatasheetElement, mapTextComponentSettingsToElement, datasheetElements]);

  const handleFieldComponentSettingsChange = React.useCallback((value: DatasheetElementSettingsFieldComponentValue) => {
    let tabOrderShouldBeUpdated = false;
    const isBulkFieldUpdate = selectedDatasheetElementsId.length > 1;
    const firstSelectedFieldId = selectedDatasheetElementsId.find((id) => {
      const element = getDatasheetElement(id);
      return element?.type === DatasheetElementType.field;
    });
    const firstSelectedFieldElement = firstSelectedFieldId
      ? (getDatasheetElement(firstSelectedFieldId) as DatasheetElement<DatasheetFieldComponentGetDto> | undefined)
      : undefined;
    const firstSelectedHeadingText = firstSelectedFieldElement
      ? mapElementToFieldComponentSettings(firstSelectedFieldElement).heading.textValue
      : undefined;
    const headingTextWasExplicitlyChanged = !isBulkFieldUpdate
      || firstSelectedHeadingText === undefined
      || value.heading.textValue !== firstSelectedHeadingText;

    const updatedElements = selectedDatasheetElementsId.reduce<Record<DatasheetElementId, DatasheetElement>>((acc, elementId) => {
      const element = getDatasheetElement(elementId) as DatasheetElement<DatasheetFieldComponentGetDto> | undefined;

      if (element?.type !== DatasheetElementType.field) {
        return acc;
      }

      if (element.original.tabOrder !== value.content.tabOrder) {
        tabOrderShouldBeUpdated = true;
      }

      const mappedSettings = mapFieldComponentSettingsToElement(value);
      const original = element.original;
      const preservedShowFieldType = original.showfieldtype ?? false;
    
      const newShowFieldType = mappedSettings.showfieldtype;
      const finalShowFieldType = newShowFieldType !== undefined && newShowFieldType !== preservedShowFieldType
        ? newShowFieldType 
        : preservedShowFieldType;
    
      const headtextValue = (mappedSettings.headtext !== undefined && mappedSettings.headtext !== null) 
        ? String(mappedSettings.headtext) 
        : (original.headtext !== undefined && original.headtext !== null ? String(original.headtext) : '');
      
      const headingpdsValue = headtextValue && headtextValue.trim() !== '' 
        ? headtextValue 
        : original.headingpds;
      
      const updatedOriginal: DatasheetFieldComponentGetDto = {
        ...original,
        ...mappedSettings,
        field: original.field,
        x: original.x,
        y: original.y,
        width: original.width,
        height: original.height,
        showfieldtype: finalShowFieldType,
      };

      if (headingTextWasExplicitlyChanged) {
        updatedOriginal.headingpds = headingpdsValue;
        updatedOriginal.headtext = headtextValue;
      } else {
        updatedOriginal.headingpds = original.headingpds;
        updatedOriginal.headtext = original.headtext;
      }

      if (value.table) {
        if (value.table.tableReportFormat !== undefined) {
          updatedOriginal.tableReportFormat = value.table.tableReportFormat ?? null;
        }
        if (value.table.dssubfields !== undefined) {
          updatedOriginal.dssubfields = value.table.dssubfields;
          updatedOriginal.m_ds_subfields = value.table.dssubfields;
          (updatedOriginal as unknown as Record<string, unknown>).subfields = undefined;
        }
        if (value.table.fieldSubfields !== undefined && updatedOriginal.field) {
          updatedOriginal.field = {
            ...updatedOriginal.field,
            subfields: value.table.fieldSubfields,
          };
        }
      }
      const updatedElement: DatasheetElement<DatasheetFieldComponentGetDto> = {
        ...element,
        original: updatedOriginal,
      };

      acc[element.id] = updatedElement;

      return acc;
    }, {});

    const mergedElements = {
      ...datasheetElements,
      ...updatedElements, 
    };

    if (tabOrderShouldBeUpdated) {
      const changedElementId = selectedDatasheetElementsId.find((id) => {
        const el = getDatasheetElement(id) as DatasheetElement<DatasheetFieldComponentGetDto> | undefined;
        if (el?.original.field?.system) return false;
        return el?.type === DatasheetElementType.field && el.original.tabOrder !== value.content.tabOrder;
      });

      if (changedElementId) {
        const changedEl = getDatasheetElement(changedElementId) as DatasheetElement<DatasheetFieldComponentGetDto>;
        const previousTabOrder = changedEl.original.tabOrder;

        const tabEligibleCount = Object.values(mergedElements).filter((el) => {
          if (el.type !== DatasheetElementType.field) return false;
          const f = (el as DatasheetElement<DatasheetFieldComponentGetDto>).original.field;
          const ft = (f as any)?.fieldtype?.typeid ?? (f as any)?.type ?? (f as any)?.rawFieldType;
          return isFieldTypeTabNavigable(ft, Boolean(f?.system));
        }).length;

        const rawNewTabOrder = value.content.tabOrder;

        if (!Number.isFinite(rawNewTabOrder) || rawNewTabOrder < 1 || tabEligibleCount === 0) {
          const revertedElements: Record<DatasheetElementId, DatasheetElement> = {
            ...mergedElements,
            [changedElementId]: {
              ...changedEl,
              original: { ...changedEl.original, tabOrder: previousTabOrder },
            } as DatasheetElement<DatasheetFieldComponentGetDto>,
          };
          onDatasheetElementsChange(revertedElements);
          return;
        }

        const newTabOrder = Math.max(1, Math.min(Math.round(rawNewTabOrder), tabEligibleCount));

        const adjustedMergedElements: Record<DatasheetElementId, DatasheetElement> =
          newTabOrder !== rawNewTabOrder
            ? {
                ...mergedElements,
                [changedElementId]: {
                  ...changedEl,
                  original: { ...changedEl.original, tabOrder: newTabOrder },
                } as DatasheetElement<DatasheetFieldComponentGetDto>,
              }
            : mergedElements;

        if (newTabOrder === previousTabOrder) {
          onDatasheetElementsChange(adjustedMergedElements);
          return;
        }

        const shiftedElements = Object.entries(adjustedMergedElements).reduce<Record<DatasheetElementId, DatasheetElement>>(
          (acc, [id, element]) => {
            if (element.type !== DatasheetElementType.field || id === changedElementId) {
              acc[id] = element;
              return acc;
            }

            const fieldEl = element as DatasheetElement<DatasheetFieldComponentGetDto>;

            if (fieldEl.original.field?.system) {
              acc[id] = element;
              return acc;
            }

            const nef = fieldEl.original.field;
            const neft = (nef as any)?.fieldtype?.typeid ?? (nef as any)?.type ?? (nef as any)?.rawFieldType;
            if (NON_TAB_FIELD_TYPES.has(neft)) {
              acc[id] = element;
              return acc;
            }

            const current = fieldEl.original.tabOrder;
            let computed = current;

            if (newTabOrder === 1 && current < previousTabOrder) {
              computed = current + 1;
            } else if (newTabOrder > 1) {
              if (current < previousTabOrder && current >= newTabOrder) {
                computed = current + 1;
              } else if (current <= newTabOrder && current > previousTabOrder) {
                computed = current - 1;
              }
            }

            const shiftedEl: DatasheetElement<DatasheetFieldComponentGetDto> = {
              ...fieldEl,
              original: { ...fieldEl.original, tabOrder: computed },
            };
            acc[id] = shiftedEl;
            return acc;
          },
          {}
        );

        onDatasheetElementsChange(shiftedElements);
        return;
      }

      onDatasheetElementsChange(normalizeDatasheetElementsTabOrder(mergedElements));
      return;
    }

    onDatasheetElementsChange(mergedElements);
  }, [selectedDatasheetElementsId, onDatasheetElementsChange, getDatasheetElement, normalizeDatasheetElementsTabOrder, datasheetElements, mapFieldComponentSettingsToElement]);

  const currentFieldComponentMetadata = React.useMemo(() => {
    const firstSelectedId = selectedDatasheetElementsId[0];
    
    if (firstSelectedId) {
      const element = getDatasheetElement(firstSelectedId);

      if (element?.type === DatasheetElementType.field) {
        return {
          fieldComponentValue: mapElementToFieldComponentSettings(element as DatasheetElement<DatasheetFieldComponentGetDto>),
          datasheetElement: element,
        }
      }
    }
  }, [selectedDatasheetElementsId, getDatasheetElement, mapElementToFieldComponentSettings]);

  const currentTextComponentValue = React.useMemo(() => {
    const firstSelectedId = selectedDatasheetElementsId[0];
    
    if (firstSelectedId) {
      const element = getDatasheetElement(firstSelectedId);

      if (element?.type === DatasheetElementType.text) {
        return mapElementToTextComponentSettings(element as DatasheetElement<DatasheetTextComponentGetDto>);
      }
    }
    return undefined;
  }, [selectedDatasheetElementsId, getDatasheetElement, mapElementToTextComponentSettings]);

  return {
    currentFieldComponentMetadata,
    currentTextComponentValue,
    mapFieldComponentSettingsToElement,
    mapElementToFieldComponentSettings,
    handleFieldComponentSettingsChange,
    handleTextComponentSettingsChange,
    mapTextComponentSettingsToElement,
    tabEligibleFieldCount,
  };
};